import { FormEvent, useEffect, useRef, useState } from "react";
import { Pencil, Plus, X } from "lucide-react";
import { NotificationModal } from "@/components/modals";
import useFloatRowInView, { floatRowStyle } from "@/hooks/useFloatRowInView";
import { api, del, errorMessage, post, put } from "@/lib/api";

type CutKey = "a" | "b" | "c" | "d";

type Scale = { a: number; b: number; c: number; d: number; saved: boolean };

type SchoolEvent = {
  id: string;
  date: string;
  kind: string;
  title: string;
  note: string | null;
};

type EventDraft = { date: string; kind: string; title: string; note: string };

type EventDialog = { mode: "add" } | { mode: "edit"; id: string };

const CUTS: { key: CutKey; letter: string }[] = [
  { key: "a", letter: "A" },
  { key: "b", letter: "B" },
  { key: "c", letter: "C" },
  { key: "d", letter: "D" },
];

const KINDS: { id: string; label: string }[] = [
  { id: "no_school", label: "No school" },
  { id: "holiday", label: "Holiday" },
  { id: "early_release", label: "Early release" },
  { id: "testing", label: "Testing" },
  { id: "other", label: "Other" },
];

const KIND_LABEL = Object.fromEntries(KINDS.map((row) => [row.id, row.label]));

const CAL_CAP = 4;

const EMPTY_DRAFT: EventDraft = { date: "", kind: "no_school", title: "", note: "" };

const DAY_DIALOG_STYLE = { width: "min(34rem, 100%)" } as const;

function dayLabel(isoDate: string) {
  const [y, m, d] = isoDate.split("-").map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function cutsDiffer(scale: Scale, baseline: Scale) {
  return CUTS.some((row) => Number(scale[row.key]) !== Number(baseline[row.key]));
}

function sortEvents(rows: SchoolEvent[]) {
  return [...rows].sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
}

export default function SchoolYearPanel({
  schoolId,
  canEditScale,
  canEditCalendar,
}: {
  schoolId: string;
  canEditScale: boolean;
  canEditCalendar: boolean;
}) {
  const [scale, setScale] = useState<Scale | null>(null);
  const [baseline, setBaseline] = useState<Scale | null>(null);
  const [editing, setEditing] = useState<CutKey | null>(null);
  const [editValue, setEditValue] = useState("");
  const [events, setEvents] = useState<SchoolEvent[] | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState("");
  const [errorScope, setErrorScope] = useState<"scale" | "day" | "">("");
  const [scaleError, setScaleError] = useState("");
  const [calendarError, setCalendarError] = useState("");
  const [scaleBusy, setScaleBusy] = useState(false);
  const [eventBusy, setEventBusy] = useState(false);
  const [draft, setDraft] = useState<EventDraft>(EMPTY_DRAFT);
  const [dialog, setDialog] = useState<EventDialog | null>(null);
  const skipCommit = useRef(false);
  const calendarRef = useRef<HTMLElement | null>(null);
  const addDaySlotRef = useRef<HTMLLIElement | null>(null);
  const pinAddDay = useFloatRowInView(
    calendarRef,
    addDaySlotRef,
    canEditCalendar && events != null,
    `${events?.length ?? 0}:${expanded}`,
  );

  useEffect(() => {
    let cancel = false;
    setScale(null);
    setBaseline(null);
    setEditing(null);
    setEvents(null);
    setExpanded(false);
    setError("");
    setErrorScope("");
    setScaleError("");
    setCalendarError("");
    setDialog(null);
    if (canEditScale) {
      api<{ success: boolean; data: Scale }>(`/api/schools/${encodeURIComponent(schoolId)}/grade-scale`)
        .then((scaleEnv) => {
          if (cancel) return;
          setScale(scaleEnv.data);
          setBaseline(scaleEnv.data);
        })
        .catch((err) => {
          if (!cancel) setScaleError(errorMessage(err));
        });
    }
    if (canEditCalendar) {
      const from = new Date();
      from.setMonth(from.getMonth() - 1);
      const to = new Date();
      to.setMonth(to.getMonth() + 14);
      const fromKey = from.toISOString().slice(0, 10);
      const toKey = to.toISOString().slice(0, 10);
      api<{ success: boolean; data: SchoolEvent[] }>(
        `/api/schools/${encodeURIComponent(schoolId)}/calendar?from=${fromKey}&to=${toKey}`,
      )
        .then((eventEnv) => {
          if (cancel) return;
          setEvents(eventEnv.data || []);
        })
        .catch((err) => {
          if (!cancel) {
            setEvents([]);
            setCalendarError(errorMessage(err));
          }
        });
    }
    return () => {
      cancel = true;
    };
  }, [schoolId, canEditScale, canEditCalendar]);

  const dirty = Boolean(scale && baseline && cutsDiffer(scale, baseline));

  const beginEdit = (key: CutKey) => {
    if (!canEditScale || !scale || scaleBusy) return;
    setEditValue(String(scale[key]));
    setEditing(key);
  };

  const commitEdit = () => {
    if (!editing) return;
    const key = editing;
    const raw = editValue.trim();
    const n = Number(raw);
    setEditing(null);
    if (raw === "" || !Number.isFinite(n)) {
      setScale((prev) => (prev && baseline ? { ...prev, [key]: baseline[key] } : prev));
      return;
    }
    const next = Math.min(100, Math.max(1, Math.round(n)));
    setScale((prev) => (prev ? { ...prev, [key]: next } : prev));
  };

  const cancelEdit = () => {
    if (!editing) return;
    const key = editing;
    skipCommit.current = true;
    setScale((prev) => (prev && baseline ? { ...prev, [key]: baseline[key] } : prev));
    setEditing(null);
  };

  const saveScale = async () => {
    if (!scale || !canEditScale || !dirty) return;
    setScaleBusy(true);
    setError("");
    setErrorScope("");
    try {
      const env = await put<{ success: boolean; data: Scale }>(
        `/api/schools/${encodeURIComponent(schoolId)}/grade-scale`,
        { a: Number(scale.a), b: Number(scale.b), c: Number(scale.c), d: Number(scale.d) },
      );
      setScale(env.data);
      setBaseline(env.data);
      setEditing(null);
    } catch (err) {
      setError(errorMessage(err));
      setErrorScope("scale");
    } finally {
      setScaleBusy(false);
    }
  };

  const openAdd = () => {
    setDraft(EMPTY_DRAFT);
    setError("");
    setErrorScope("");
    setDialog({ mode: "add" });
  };

  const openEdit = (row: SchoolEvent) => {
    setDraft({ date: row.date, kind: row.kind, title: row.title, note: row.note || "" });
    setError("");
    setErrorScope("");
    setDialog({ mode: "edit", id: row.id });
  };

  const closeDialog = () => {
    if (eventBusy) return;
    setDialog(null);
  };

  const saveDay = async (event: FormEvent) => {
    event.preventDefault();
    if (!canEditCalendar || !dialog) return;
    setEventBusy(true);
    setError("");
    setErrorScope("");
    try {
      if (dialog.mode === "edit") {
        const env = await put<{ success: boolean; data: SchoolEvent }>(
          `/api/schools/${encodeURIComponent(schoolId)}/calendar/${encodeURIComponent(dialog.id)}`,
          draft,
        );
        setEvents((prev) => sortEvents((prev || []).map((row) => (row.id === env.data.id ? env.data : row))));
      } else {
        const env = await post<{ success: boolean; data: SchoolEvent }>(
          `/api/schools/${encodeURIComponent(schoolId)}/calendar`,
          draft,
        );
        setEvents((prev) => sortEvents([...(prev || []), env.data]));
      }
      setDialog(null);
      setDraft(EMPTY_DRAFT);
    } catch (err) {
      setError(errorMessage(err));
      setErrorScope("day");
    } finally {
      setEventBusy(false);
    }
  };

  const removeDay = async (id: string) => {
    if (!canEditCalendar) return;
    setError("");
    setErrorScope("");
    try {
      await del(`/api/schools/${encodeURIComponent(schoolId)}/calendar/${encodeURIComponent(id)}`);
      setEvents((prev) => (prev || []).filter((row) => row.id !== id));
    } catch (err) {
      setError(errorMessage(err));
      setErrorScope("day");
    }
  };

  const visibleEvents = events ? (expanded ? events : events.slice(0, CAL_CAP)) : [];
  const hiddenCount = events ? Math.max(0, events.length - CAL_CAP) : 0;

  return (
    <>
      {canEditScale ? (
      <section className="al-school-manage-panel" id="al-school-grades">
        <div className="al-school-manage-panel-head">
          <h2 className="al-school-manage-panel-title">Grade scale</h2>
        </div>
        <p className="al-school-manage-hint">
          Every class here uses these letters.
          {canEditScale ? " Double-click a number to change it." : ""}
        </p>
        {scaleError || (error && errorScope === "scale") ? (
          <p className="al-school-manage-hint">{scaleError || error}</p>
        ) : null}
        {!scale ? (
          scaleError ? null : <p className="al-school-manage-hint">Loading the scale…</p>
        ) : (
          <div className="al-school-scale">
            {CUTS.map(({ key, letter }) => (
              <span key={key} className="al-school-scale__cut">
                {letter} at
                {canEditScale && editing === key ? (
                  <input
                    className="al-school-scale__input"
                    type="number"
                    min={1}
                    max={100}
                    autoFocus
                    aria-label={`${letter} at`}
                    disabled={scaleBusy}
                    value={editValue}
                    onChange={(event) => {
                      const raw = event.target.value;
                      setEditValue(raw);
                      const n = Number(raw);
                      if (raw.trim() !== "" && Number.isFinite(n)) {
                        const next = Math.min(100, Math.max(1, Math.round(n)));
                        setScale((prev) => (prev ? { ...prev, [key]: next } : prev));
                      }
                    }}
                    onBlur={() => {
                      if (skipCommit.current) {
                        skipCommit.current = false;
                        return;
                      }
                      commitEdit();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        commitEdit();
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        cancelEdit();
                      }
                    }}
                  />
                ) : (
                  <span
                    className={`al-school-scale__badge${canEditScale ? " is-editable" : ""}`}
                    title={canEditScale ? "Double-click to edit" : undefined}
                    onDoubleClick={() => beginEdit(key)}
                  >
                    {scale[key]}
                  </span>
                )}
                %
              </span>
            ))}
            <span className="al-school-scale__f">F is below D</span>
            {canEditScale && dirty ? (
              <button
                type="button"
                className="al-schools-btn al-school-scale__save"
                disabled={scaleBusy}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void saveScale()}
              >
                {scaleBusy ? "Saving…" : "Save"}
              </button>
            ) : null}
          </div>
        )}
      </section>
      ) : null}

      {canEditCalendar ? (
      <section className="al-school-manage-panel" id="al-school-calendar" ref={calendarRef}>
        <div className="al-school-manage-panel-head">
          <h2 className="al-school-manage-panel-title">School calendar</h2>
        </div>
        <p className="al-school-manage-hint">
          Holidays, days off, early release, and testing days.
        </p>
        {calendarError || (error && errorScope === "day" && !dialog) ? (
          <p className="al-school-manage-hint">{calendarError || error}</p>
        ) : null}
        {!events ? (
          <p className="al-school-manage-hint">Loading the calendar…</p>
        ) : events.length === 0 && !canEditCalendar ? (
          <p className="al-school-manage-hint">No school days yet.</p>
        ) : (
          <ul className="al-school-cal-list">
            {visibleEvents.map((row) => (
              <li key={row.id}>
                <span className="al-school-cal-line">
                  <strong>{row.title}</strong>
                  <span>
                    {dayLabel(row.date)} · {KIND_LABEL[row.kind] || "School day"}
                    {row.note ? ` · ${row.note}` : ""}
                  </span>
                </span>
                {canEditCalendar ? (
                  <span className="al-school-cal-actions">
                    <button
                      type="button"
                      className="al-school-cal-icon"
                      aria-label={`Edit ${row.title}`}
                      title="Edit"
                      onClick={() => openEdit(row)}
                    >
                      <Pencil size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="al-school-cal-icon"
                      aria-label={`Remove ${row.title}`}
                      title="Remove"
                      onClick={() => void removeDay(row.id)}
                    >
                      <X size={13} aria-hidden="true" />
                    </button>
                  </span>
                ) : null}
              </li>
            ))}
            {hiddenCount > 0 ? (
              <li className="al-school-cal-expand-row">
                <button type="button" className="al-school-cal-expand" onClick={() => setExpanded((open) => !open)}>
                  {expanded ? "Show less" : `Show ${hiddenCount} more`}
                </button>
              </li>
            ) : null}
            {canEditCalendar ? (
              <li
                ref={addDaySlotRef}
                className="al-school-cal-add"
                style={pinAddDay ? { height: pinAddDay.height } : undefined}
              >
                <button
                  type="button"
                  className={pinAddDay ? "is-float" : undefined}
                  style={floatRowStyle(pinAddDay)}
                  onClick={openAdd}
                >
                  <Plus size={15} aria-hidden="true" />
                  Add day
                </button>
              </li>
            ) : null}
          </ul>
        )}
      </section>
      ) : null}

      <NotificationModal
        isOpen={dialog != null}
        type="neutral"
        icon={dialog?.mode === "edit" ? <Pencil /> : <Plus />}
        title={dialog?.mode === "edit" ? "Edit day" : "Add day"}
        message={null}
        overlay
        overlayClose={!eventBusy}
        escapeClose={!eventBusy}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={DAY_DIALOG_STYLE}
        onClose={closeDialog}
      >
        {error && dialog ? <p className="al-school-manage-hint">{error}</p> : null}
        <form className="al-schools-form" onSubmit={(event) => void saveDay(event)}>
          <label className="al-schools-label" htmlFor="cal-date">
            Date
          </label>
          <input
            id="cal-date"
            className="al-schools-input"
            type="date"
            required
            autoFocus
            disabled={eventBusy}
            value={draft.date}
            onChange={(event) => setDraft((prev) => ({ ...prev, date: event.target.value }))}
          />
          <label className="al-schools-label" htmlFor="cal-kind">
            School day
          </label>
          <select
            id="cal-kind"
            className="al-schools-input"
            disabled={eventBusy}
            value={draft.kind}
            onChange={(event) => setDraft((prev) => ({ ...prev, kind: event.target.value }))}
          >
            {KINDS.map((kind) => (
              <option key={kind.id} value={kind.id}>
                {kind.label}
              </option>
            ))}
          </select>
          <label className="al-schools-label" htmlFor="cal-title">
            Title
          </label>
          <input
            id="cal-title"
            className="al-schools-input"
            placeholder="Labor Day"
            required
            maxLength={80}
            disabled={eventBusy}
            value={draft.title}
            onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
          />
          <label className="al-schools-label" htmlFor="cal-note">
            Note
          </label>
          <input
            id="cal-note"
            className="al-schools-input"
            placeholder="Optional"
            maxLength={200}
            disabled={eventBusy}
            value={draft.note}
            onChange={(event) => setDraft((prev) => ({ ...prev, note: event.target.value }))}
          />
          <div className="al-schools-form-actions">
            <button type="button" className="al-schools-btn al-schools-btn--ghost" disabled={eventBusy} onClick={closeDialog}>
              Cancel
            </button>
            <button type="submit" className="al-schools-btn" disabled={eventBusy}>
              {eventBusy ? "Saving…" : dialog?.mode === "edit" ? "Save" : "Add day"}
            </button>
          </div>
        </form>
      </NotificationModal>
    </>
  );
}
